import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    TextField, Button, Stack, Typography, Alert,
    Box, Autocomplete, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
    Checkbox, FormControlLabel, MenuItem
} from '@mui/material';
import { crearTramiteART, getEvaluacionArtCliente } from '../../services/art/tramiteARTService';
import { modificarTramite, getTramites, guardarMotivoComplementario } from '../../services/tramiteService';
import { buscarClientes, getClientePorCuil } from '../../services/clienteService';
import ModalRegistrarCliente from '../../components/ModalRegistrarCliente';
import { useAuth } from '../../auth/AuthContext';
import CampoFechaLiquidar from '../../components/Tramite/CampoFechaLiquidar';
import PageHeader from '../../components/ui/PageHeader';

/**
 * Formulario exclusivo para iniciar trámites ART.
 * Se puede usar standalone o dentro de un modal (recibe onSuccess / onCancel).
 *
 * Props:
 *  - onSuccess(tramiteCreado): callback tras creación exitosa.
 *  - onCancel(): callback al cancelar.
 *  - cuilInicial?: CUIL pre-cargado (opcional).
 */
// onIniciarComplementario: ver FormularioIniciarTramiteRTI. Desde la ficha de atención, "Sí, iniciar RTI"
// abre el RTI en la ficha con el mismo turno de origen en vez de navegar a la página suelta.
export default function FormularioIniciarTramiteART({ onSuccess, onCancel, cuilInicial, idTurnoOrigen, vieneDeOtroTramite = false, onIniciarComplementario }) {
    // idTipoTramite = 2 (ART) — hardcodeado, sin selector
    const ID_TIPO_ART = 2;
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const pendingTramiteId = location.state?.pendingTramiteId;
    const clienteInicial = location.state?.clienteInicial || location.state?.cliente;
    const { cuil: cuilParam } = useParams();
    const cuilEfectivo = cuilInicial?.cuil || cuilInicial?.cuilCliente || cuilInicial?.dni || cuilInicial || cuilParam;
    const vieneDeOtroTramiteState = Boolean(location.state?.vieneDeOtroTramite || location.state?.flujoCruzado);
    const bloquearCreacionComplementaria = Boolean(vieneDeOtroTramite || vieneDeOtroTramiteState);

    const CLASIFICACIONES_ART = [
        { value: 'ACCIDENTE_LABORAL',  label: 'Accidente Laboral' },
        { value: 'ENFERMEDAD_LABORAL', label: 'Enfermedad Laboral' },
        { value: 'AMBAS',              label: 'Ambas' },
    ];

    const [form, setForm] = useState({
        idCliente: '',
        fechaInicioEstimada: '',
        idTipoTramite: ID_TIPO_ART,
        comentarios: '',
        idUsuario: 1,
        plazo: '',
        fechaBaja: '',
        fechaInicio: '',
        clasificacion: '',
        organismo: '',
        // Campos propios de la Caratula de ART (ver modulos/caratula)
        jornadaLaboral: '',
        accidentesAnteriores: '',
        parteCuerpoDanada: '',
    });

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Dialog complementario RTI
    const [dialogoComplementario, setDialogoComplementario] = useState(false);
    const [tramiteArtCreado, setTramiteArtCreado] = useState(null);
    const [pasoComplementario, setPasoComplementario] = useState(1);
    const [motivoSinRTI, setMotivoSinRTI] = useState('');
    const [guardandoMotivo, setGuardandoMotivo] = useState(false);
    const [cargandoEvaluacionArt, setCargandoEvaluacionArt] = useState(false);
    const [evaluacionArt, setEvaluacionArt] = useState(null);
    const [tieneDuplicadoART, setTieneDuplicadoART] = useState(false);
    const [cargandoDuplicados, setCargandoDuplicados] = useState(false);
    const [confirmarDuplicado, setConfirmarDuplicado] = useState(false);

    // Búsqueda de cliente
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [opcionesCliente, setOpcionesCliente] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [modalClienteOpen, setModalClienteOpen] = useState(false);

    // Carga inicial
    useEffect(() => {
        const cargarDatos = async () => {
            try {
                if (clienteInicial?.cuil || clienteInicial?.cuilCliente || clienteInicial?.dni) {
                    const cliente = clienteInicial;
                    setClienteSeleccionado(cliente);
                    setForm(prev => ({ ...prev, idCliente: cliente.cuil || cliente.cuilCliente || cliente.dni || '' }));
                    if (cliente.cuil || cliente.cuilCliente) {
                        await evaluarPlazoArt(cliente.cuil || cliente.cuilCliente);
                    }
                    return;
                }
                if (cuilEfectivo) {
                    const cliente = await getClientePorCuil(cuilEfectivo);
                    setClienteSeleccionado(cliente);
                    setForm(prev => ({ ...prev, idCliente: cliente.cuil }));
                    await evaluarPlazoArt(cliente.cuil);
                }
            } catch (err) {
                console.error('Error al cargar datos iniciales ART:', err);
            }
        };
        cargarDatos();
    }, [cuilEfectivo, clienteInicial]);

    const evaluarPlazoArt = async (cuil) => {
        if (!cuil) {
            setEvaluacionArt(null);
            setForm(prev => ({ ...prev, plazo: '' }));
            return;
        }
        setCargandoEvaluacionArt(true);
        try {
            const data = await getEvaluacionArtCliente(cuil);
            setEvaluacionArt(data);
            const ultimoMes = data?.art?.periodoUltimaRelacionHasta; // "YYYY-MM"
            let fechaBajaCalculada = '';
            if (ultimoMes) {
                const [anio, mes] = ultimoMes.split('-').map(Number);
                const ultimoDia = new Date(anio, mes, 0).getDate();
                fechaBajaCalculada = `${anio}-${String(mes).padStart(2, '0')}-${String(ultimoDia).padStart(2, '0')}`;
            }
            setForm(prev => ({
                ...prev,
                plazo: data?.plazoRestanteMeses != null ? Number(data.plazoRestanteMeses) : '',
                fechaBaja: prev.fechaBaja || fechaBajaCalculada,
            }));
        } catch (err) {
            console.error('Error al evaluar plazo ART:', err);
            setEvaluacionArt(null);
            setForm(prev => ({ ...prev, plazo: '' }));
        } finally {
            setCargandoEvaluacionArt(false);
        }
    };

    const busquedaTimeoutRef = useRef(null);

    // Búsqueda de clientes con debounce: espera 300ms tras el último tecleo antes de llamar a la API
    const handleBusquedaCliente = (_event, value) => {
        if (!value) { setOpcionesCliente([]); setBuscando(false); return; }
        setBuscando(true);
        if (busquedaTimeoutRef.current) clearTimeout(busquedaTimeoutRef.current);
        busquedaTimeoutRef.current = setTimeout(async () => {
            try {
                setOpcionesCliente(await buscarClientes(value));
            } catch (err) {
                console.error('Error al buscar clientes:', err);
            } finally {
                setBuscando(false);
            }
        }, 300);
    };

    const handleSeleccionCliente = (_event, newValue) => {
        setClienteSeleccionado(newValue);
        setForm(prev => ({ ...prev, idCliente: newValue ? newValue.cuil : '' }));
        evaluarPlazoArt(newValue?.cuil || '');
    };

    const handleRegistroExitoso = (clienteNuevo) => {
        setClienteSeleccionado(clienteNuevo);
        setForm(prev => ({ ...prev, idCliente: clienteNuevo.cuil }));
        evaluarPlazoArt(clienteNuevo.cuil);
        setModalClienteOpen(false);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    useEffect(() => {
        const cuilCliente = form.idCliente;
        if (!cuilCliente) {
            setTieneDuplicadoART(false);
            setConfirmarDuplicado(false);
            return;
        }

        let activo = true;

        const cargarDuplicados = async () => {
            setCargandoDuplicados(true);
            try {
                const tramites = await getTramites(0, 200, cuilCliente, null, null, '');
                const items = Array.isArray(tramites) ? tramites : (tramites?.content || []);

                const esTramiteART = (t) => {
                    const idTipo = Number(t?.idTipoTramite);
                    const nombreTipo = String(
                        t?.nombreTipoTramite || t?.tipoTramite?.nombre || t?.tipoTramite || ''
                    ).toLowerCase();
                    return idTipo === ID_TIPO_ART || nombreTipo.includes('art');
                };

                const esTramiteActivo = (t) => {
                    const estado = String(t?.nombreEstadoActual || t?.estadoActual || '').toLowerCase();
                    if (!estado) return true;
                    return !['finaliz', 'cancel', 'rechaz', 'cerrad', 'baja'].some(token => estado.includes(token));
                };

                const existeDuplicado = items.some((t) =>
                    Number(t?.idTramite) !== Number(pendingTramiteId) && esTramiteART(t) && esTramiteActivo(t)
                );

                if (activo) {
                    setTieneDuplicadoART(existeDuplicado);
                    if (!existeDuplicado) {
                        setConfirmarDuplicado(false);
                    }
                }
            } catch (err) {
                console.error('Error validando trámites ART duplicados:', err);
                if (activo) {
                    setTieneDuplicadoART(false);
                }
            } finally {
                if (activo) {
                    setCargandoDuplicados(false);
                }
            }
        };

        cargarDuplicados();
        return () => {
            activo = false;
        };
    }, [form.idCliente, pendingTramiteId]);

    const finalizarFlujo = (tramiteCreado) => {
        if (onSuccess) {
            onSuccess(tramiteCreado);
            return;
        }
        navigate(`/art/tramites/${tramiteCreado.idTramite}`);
    };

    const abrirDialogoComplementario = (tramiteCreado) => {
        setTramiteArtCreado(tramiteCreado);
        setPasoComplementario(1);
        setMotivoSinRTI('');
        setDialogoComplementario(true);
    };

    const generarFirmaComentario = () => {
        const ahora = new Date();
        const dd = String(ahora.getDate()).padStart(2, '0');
        const mm = String(ahora.getMonth() + 1).padStart(2, '0');
        const yyyy = ahora.getFullYear();
        const hh = String(ahora.getHours()).padStart(2, '0');
        const min = String(ahora.getMinutes()).padStart(2, '0');
        return `[${dd}/${mm}/${yyyy} ${hh}:${min} - ${user?.nombre || 'Usuario'}]:`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.idCliente) {
            setError('Debe seleccionar un cliente.');
            return;
        }
        if (tieneDuplicadoART && !confirmarDuplicado) {
            setError('Debe confirmar que desea crear un nuevo trámite ART para un cliente con trámite activo.');
            return;
        }

        setError('');
        setSuccess('');
        setSubmitting(true);

        try {
            if (pendingTramiteId) {
                const rolUsuario = String(user?.rol || '')
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .toUpperCase();
                const comentarioInicio = rolUsuario === 'RECEPCION'
                    ? `Tramite iniciado por RECEPCION${form.comentarios ? ` | ${form.comentarios}` : ''}`
                    : form.comentarios;

                const tramiteActualizado = await modificarTramite(pendingTramiteId, {
                    idTipoTramite: ID_TIPO_ART,
                    comentarios: comentarioInicio,
                    idCliente: form.idCliente,
                    idUsuario: form.idUsuario
                });

                if (window.recargarCampanaNotificaciones) {
                    window.recargarCampanaNotificaciones();
                }

                setSuccess(`Trámite ART iniciado con éxito. ID: ${tramiteActualizado.idTramite}`);
                if (bloquearCreacionComplementaria) {
                    finalizarFlujo(tramiteActualizado);
                } else {
                    abrirDialogoComplementario(tramiteActualizado);
                }
            } else {
                const payload = {
                    idCliente: form.idCliente,
                    idTipoTramite: ID_TIPO_ART,
                    comentarios: form.comentarios,
                    idUsuario: form.idUsuario,
                    plazo: form.plazo !== '' ? Number(form.plazo) : null,
                    fechaBaja: form.fechaBaja || null,
                    fechaInicio: form.fechaInicio || null,
                    clasificacion: form.clasificacion || null,
                    jornadaLaboral: form.jornadaLaboral || null,
                    accidentesAnteriores: form.accidentesAnteriores || null,
                    parteCuerpoDanada: form.parteCuerpoDanada || null,
                    organismo: form.organismo || null,
                    fechaInicioEstimada: form.fechaInicioEstimada || null,
                    idTurnoOrigen: idTurnoOrigen || null,
                };

                const tramiteNuevo = await crearTramiteART(payload);
                if (window.recargarCampanaNotificaciones) {
                    window.recargarCampanaNotificaciones();
                }
                setSuccess(`Trámite ART creado con éxito. ID: ${tramiteNuevo.idTramite}`);
                if (bloquearCreacionComplementaria) {
                    finalizarFlujo(tramiteNuevo);
                } else {
                    abrirDialogoComplementario(tramiteNuevo);
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error al crear el trámite ART.');
        } finally {
            setSubmitting(false);
        }
    };

    // Si se usa dentro de un modal, no mostrar el fondo degradado propio
    const esModal = !!onCancel;

    const contenido = (
        <>
            <ModalRegistrarCliente
                open={modalClienteOpen}
                onClose={() => setModalClienteOpen(false)}
                onSuccess={handleRegistroExitoso}
                mostrarDialogoConfirmacion={false}
            />

            <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                    {/* ── Sección: Seleccionar Cliente ── */}
                    <div style={{
                        background: 'var(--primary-bg)',
                        padding: '1rem 1.5rem',
                        borderRadius: '12px',
                        border: '1px solid var(--primary-border)'
                    }}>
                        <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--primary-text)' }}>
                            <i className="bi bi-person-check me-2"></i>Seleccionar Cliente
                        </Typography>
                    </div>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <Autocomplete
                            id="buscar-cliente-art"
                            options={opcionesCliente}
                            getOptionLabel={(opt) => `${opt.nombre} ${opt.apellido} (${opt.cuil})`}
                            onInputChange={handleBusquedaCliente}
                            onChange={handleSeleccionCliente}
                            value={clienteSeleccionado}
                            isOptionEqualToValue={(opt, val) => opt.cuil === val.cuil}
                            noOptionsText="No se encontraron clientes"
                            loading={buscando}
                            loadingText="Buscando..."
                            sx={{ flexGrow: 1 }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Buscar cliente por CUIL, Apellido o Nombre"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {buscando ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                        <Button
                            variant="outlined"
                            onClick={() => setModalClienteOpen(true)}
                            sx={{ height: '56px', flexShrink: 0 }}
                        >
                            Registrar Cliente
                        </Button>
                    </Box>

                    {/* ── Sección: Datos específicos ART ── */}
                    <div style={{
                        background: 'var(--primary-bg)',
                        padding: '1rem 1.5rem',
                        borderRadius: '12px',
                        border: '1px solid var(--primary-border)',
                        marginTop: '1.5rem'
                    }}>
                        <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--primary-text)' }}>
                            <i className="bi bi-heart-pulse me-2"></i>Datos del Trámite ART
                        </Typography>
                    </div>

                    {/* Plazo */}
                    <TextField
                        label="Plazo (en meses)"
                        name="plazo"
                        type="number"
                        value={form.plazo}
                        onChange={handleChange}
                        fullWidth
                        InputProps={{
                            readOnly: true,
                            endAdornment: cargandoEvaluacionArt ? <CircularProgress size={18} /> : null
                        }}
                        inputProps={{ min: 0, placeholder: "Se calcula automáticamente" }}
                        helperText="Se calcula automáticamente según aportes del cliente (última relación de dependencia)."
                    />

                    <TextField
                        label="Fecha de baja exacta (opcional)"
                        name="fechaBaja"
                        type="date"
                        value={form.fechaBaja}
                        onChange={handleChange}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        helperText="Si la conoce, ingrese el día exacto de baja laboral para calcular el vencimiento con precisión de días."
                    />

                    <TextField
                        label="Fecha de inicio del trámite (opcional)"
                        name="fechaInicio"
                        type="date"
                        value={form.fechaInicio}
                        onChange={handleChange}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        helperText="Si el trámite fue iniciado antes de cargarlo al sistema, ingresá aquí la fecha real. Determina en qué mes aparece agrupado en el listado."
                    />

                    <TextField
                        select
                        label="Organismo"
                        name="organismo"
                        value={form.organismo}
                        onChange={handleChange}
                        fullWidth
                        helperText="Seleccione si el trámite se gestiona ante Provincia o ANSES."
                    >
                        <MenuItem value=""><em>Sin especificar</em></MenuItem>
                        <MenuItem value="PROVINCIA">Provincia</MenuItem>
                        <MenuItem value="ANSES">ANSES</MenuItem>
                    </TextField>

                    <TextField
                        select
                        label="Clasificación del Trámite ART"
                        name="clasificacion"
                        value={form.clasificacion}
                        onChange={handleChange}
                        fullWidth
                        helperText="Seleccione si se trata de un accidente laboral, enfermedad laboral o ambas."
                    >
                        <MenuItem value=""><em>Sin clasificar</em></MenuItem>
                        {CLASIFICACIONES_ART.map(({ value, label }) => (
                            <MenuItem key={value} value={value}>{label}</MenuItem>
                        ))}
                    </TextField>

                    {/* Campos propios de la Caratula de ART */}
                    <TextField
                        label="Jornada laboral"
                        name="jornadaLaboral"
                        value={form.jornadaLaboral}
                        onChange={handleChange}
                        fullWidth
                        helperText="Ej: 3 veces por semana x 24hs. Sale impreso en la caratula."
                    />

                    <TextField
                        label="Tuvo accidentes anteriores"
                        name="accidentesAnteriores"
                        value={form.accidentesAnteriores}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={2}
                    />

                    <TextField
                        label="Parte del cuerpo mas danada"
                        name="parteCuerpoDanada"
                        value={form.parteCuerpoDanada}
                        onChange={handleChange}
                        fullWidth
                    />

                    {evaluacionArt?.art && (
                        <Alert severity={evaluacionArt.art.puedeIniciarTramite ? 'success' : 'warning'} sx={{ borderRadius: '10px' }}>
                            {evaluacionArt.art.mensaje}
                            {evaluacionArt.art.periodoUltimaRelacionHasta && (
                                <div style={{ marginTop: '0.4rem' }}>
                                    Última relación: {evaluacionArt.art.periodoUltimaRelacionDesde} a {evaluacionArt.art.periodoUltimaRelacionHasta}
                                    {evaluacionArt.art.ultimoEmpleador ? ` · ${evaluacionArt.art.ultimoEmpleador}` : ''}
                                </div>
                            )}
                        </Alert>
                    )}


                    {/* ── Sección: Comentarios ── */}
                    <div style={{
                        background: 'var(--primary-bg)',
                        padding: '1rem 1.5rem',
                        borderRadius: '12px',
                        border: '1px solid var(--primary-border)',
                        marginTop: '1.5rem'
                    }}>
                        <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--primary-text)' }}>
                            <i className="bi bi-chat-text me-2"></i>Comentarios
                        </Typography>
                    </div>

                    <CampoFechaLiquidar
                        valor={form.fechaInicioEstimada}
                        onChange={(v) => setForm((f) => ({ ...f, fechaInicioEstimada: v }))}
                    />

                    <TextField
                        label="Comentarios Iniciales"
                        name="comentarios"
                        value={form.comentarios}
                        onChange={handleChange}
                        multiline
                        rows={4}
                        fullWidth
                    />

                    {/* Alertas */}
                    {error && <Alert severity="error" sx={{ borderRadius: '10px' }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ borderRadius: '10px' }}>{success}</Alert>}
                    {tieneDuplicadoART && (
                        <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                            Este cliente ya posee un trámite de ART en el sistema. ¿Está seguro de que desea crear otro?
                        </Alert>
                    )}
                    {tieneDuplicadoART && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={confirmarDuplicado}
                                    onChange={(e) => setConfirmarDuplicado(e.target.checked)}
                                />
                            }
                            label="Confirmo que deseo continuar con un nuevo trámite ART igualmente."
                        />
                    )}
                    {cargandoDuplicados && <Alert severity="info" sx={{ borderRadius: '10px' }}>Verificando trámites activos del cliente...</Alert>}

                    {/* Botones */}
                    <Stack direction="row" spacing={2} sx={{ width: '100%', marginTop: '2rem' }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            size="large"
                            onClick={() => onCancel ? onCancel() : window.history.back()}
                            sx={{
                                background: 'white',
                                color: '#dc2626',
                                border: '2px solid #fecaca',
                                borderRadius: '10px',
                                padding: '0.75rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                '&:hover': { background: '#fef2f2', border: '2px solid #fca5a5' }
                            }}
                        >
                            <i className="bi bi-x-circle me-2"></i>
                            Cancelar
                        </Button>
                        <Button
                            fullWidth
                            type="submit"
                            size="large"
                            disabled={!form.idCliente || submitting}
                            sx={{
                                background: 'var(--primary)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '10px',
                                padding: '0.75rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                boxShadow: 'var(--shadow-md)',
                                '&:hover': {
                                    background: 'var(--primary)',
                                    boxShadow: 'var(--shadow-md)'
                                },
                                '&:disabled': { background: '#e5e7eb', color: '#9ca3af' }
                            }}
                        >
                            {submitting ? (
                                <CircularProgress size={24} sx={{ color: 'white' }} />
                            ) : (
                                <>
                                    <i className="bi bi-check-circle me-2"></i>
                                    Iniciar Trámite ART
                                </>
                            )}
                        </Button>
                    </Stack>
                </Stack>
            </form>
        </>
    );

    const dialogoComplementarioJsx = (
        <Dialog open={dialogoComplementario} maxWidth="sm" fullWidth className="dialog-centrado-contenido" PaperProps={{ sx: { borderRadius: '16px' } }}>
            {pasoComplementario === 1 && (
                <>
                    <DialogTitle sx={{ fontWeight: 'bold', color: '#15803d', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <i className="bi bi-check-circle-fill" style={{ color: '#22c55e' }}></i>
                        Trámite ART creado
                    </DialogTitle>
                    <DialogContent>
                        <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }}>
                            Trámite ART #{tramiteArtCreado?.idTramite} creado con éxito.
                        </Alert>
                        <Typography variant="body1" sx={{ color: '#374151' }}>
                            ¿Desea iniciar también un trámite <strong>RTI</strong> para este cliente?
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ p: 2, gap: 1 }}>
                        <Button onClick={() => setPasoComplementario(2)} sx={{ color: '#6b7280', textTransform: 'none' }}>
                            No
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                setDialogoComplementario(false);
                                if (onIniciarComplementario) {
                                    onIniciarComplementario(tramiteArtCreado);
                                    return;
                                }
                                if (onCancel) {
                                    onCancel();
                                }
                                const cuilCliente = tramiteArtCreado?.cliente?.cuil || tramiteArtCreado?.idCliente || form.idCliente;
                                navigate(`/rti/tramites/iniciar-tramite/${cuilCliente}`, {
                                    state: {
                                        cuil: cuilCliente,
                                        vieneDeOtroTramite: true,
                                        flujoCruzado: true,
                                        areaOrigen: 'ART',
                                        tramiteOrigenId: tramiteArtCreado?.idTramite,
                                    }
                                });
                            }}
                            sx={{ background: 'var(--primary)', borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                        >
                            <i className="bi bi-file-earmark-medical me-2"></i>
                            Sí, iniciar RTI
                        </Button>
                    </DialogActions>
                </>
            )}
            {pasoComplementario === 2 && (
                <>
                    <DialogTitle sx={{ fontWeight: 'bold', color: '#1e40af' }}>
                        Motivo por el cual no se inicia RTI
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="#64748b" sx={{ mb: 2 }}>
                            Indique el motivo por el cual no se inicia también el trámite RTI. Quedará registrado en el trámite ART.
                        </Typography>
                        <TextField
                            label="Motivo"
                            value={motivoSinRTI}
                            onChange={(e) => setMotivoSinRTI(e.target.value)}
                            multiline
                            rows={3}
                            fullWidth
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: 2, gap: 1 }}>
                        <Button onClick={() => setPasoComplementario(1)} sx={{ color: '#6b7280', textTransform: 'none' }}>
                            <i className="bi bi-arrow-left me-1"></i> Volver
                        </Button>
                        <Button onClick={() => { setDialogoComplementario(false); finalizarFlujo(tramiteArtCreado); }} sx={{ color: '#6b7280', textTransform: 'none' }}>
                            Omitir y continuar
                        </Button>
                        <Button
                            variant="contained"
                            disabled={!motivoSinRTI.trim() || guardandoMotivo}
                            onClick={async () => {
                                setGuardandoMotivo(true);
                                try {
                                    const firma = generarFirmaComentario();
                                    const { tramiteActualizado, comentariosActualizados } = await guardarMotivoComplementario({
                                        idTramite: tramiteArtCreado.idTramite,
                                        idCliente: tramiteArtCreado?.idCliente || form.idCliente,
                                        idTipoTramite: ID_TIPO_ART,
                                        idUsuario: form.idUsuario,
                                        versionFallback: tramiteArtCreado?.version ?? 0,
                                        indicador: '[MOTIVO NO INICIO RTI]',
                                        motivo: motivoSinRTI.trim(),
                                        firma,
                                        comentariosFallback: (form.comentarios ?? '').trim()
                                            || (tramiteArtCreado?.comentarios ?? '').trim()
                                            || (tramiteArtCreado?.comentario ?? '').trim()
                                    });
                                    setDialogoComplementario(false);
                                    finalizarFlujo({
                                        ...tramiteArtCreado,
                                        ...tramiteActualizado,
                                        comentarios: comentariosActualizados
                                    });
                                } catch (err) {
                                    console.error('Error guardando motivo:', err);
                                    setError('No se pudo guardar el motivo por un conflicto de edición. Intente nuevamente.');
                                } finally {
                                    setGuardandoMotivo(false);
                                }
                            }}
                            sx={{ background: 'var(--primary)', borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                        >
                            {guardandoMotivo ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Confirmar'}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );

    // Si se usa como standalone (no en modal), envolver con el mismo layout del formulario original
    if (!esModal) {
        return (
            <div className="area-art">
                <Box sx={{
                    background: 'var(--bg-page)',
                    minHeight: '100vh',
                    padding: 3
                }}>
                    <Box sx={{
                        maxWidth: 900,
                        margin: 'auto',
                        background: 'white',
                        borderRadius: '16px',
                        boxShadow: 'var(--shadow-md)',
                        overflow: 'hidden'
                    }}>
                        <PageHeader
                            variante="banner"
                            icono="heart-pulse"
                            titulo="Iniciar Trámite ART"
                            descripcion="Complete los datos para registrar un nuevo trámite de ART"
                        />

                        <Box sx={{ padding: '2rem' }}>
                            {contenido}
                        </Box>
                    </Box>
                </Box>
                {dialogoComplementarioJsx}
            </div>
        );
    }

    // Dentro de un modal: el contenido se limita a la altura del modal contenedor
    // y scrollea internamente (sin esto, el formulario ART -que suma helper text
    // en cada campo, alertas de evaluación/duplicados y comentarios- puede terminar
    // siendo más alto que el modal que lo envuelve).
    return (
        <div className="area-art">
            <Box sx={{ maxHeight: '80vh', overflowY: 'auto', p: 3 }}>
                {contenido}
            </Box>
            {dialogoComplementarioJsx}
        </div>
    );
}